import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, X, ArrowRight, ArrowLeft, Wifi, DoorOpen, QrCode,
  Sparkles, Check, MapPin, Home as HomeIcon, Copy, Link2, PencilRuler,
  Share2, CheckCircle2, MessageCircle,
} from "lucide-react";
import { DEMO } from "@/lib/demoImages";
import { CTA_PRIMARY } from "@/lib/plans";

const STEP_MS = 6000;

/* ---------- Realistic phone frame ---------- */
function Phone({ children }) {
  return (
    <div className="h-full aspect-[9/19] rounded-[2.2rem] bg-slate-900 p-2 shadow-2xl shadow-black/50 shrink-0">
      <div className="w-full h-full rounded-[1.8rem] overflow-hidden bg-[#FDFBF7] relative">{children}</div>
    </div>
  );
}

const fade = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.98 },
  transition: { duration: 0.45, ease: "easeOut" },
};

/* ---------- Scenes (look like the real product) ---------- */

// 1. Create
function SceneCreate() {
  const opts = [
    { icon: Sparkles, label: "Crea con l'AI", note: "Consigliato", solid: true },
    { icon: Link2, label: "Importa da annuncio" },
    { icon: PencilRuler, label: "Crea manualmente" },
  ];
  return (
    <div className="h-full flex flex-col p-4">
      <p className="font-serif-display text-lg text-[#1F2A44]">Nuova struttura</p>
      <p className="text-[11px] text-[#7A736A] mb-3">Come vuoi iniziare?</p>
      <div className="space-y-2.5">
        {opts.map((o, i) => (
          <div key={o.label}
            className={`rounded-2xl p-3 flex items-center gap-2.5 ${o.solid ? "border-2 border-blue-500 bg-blue-50/70" : "border border-slate-200 bg-white"}`}>
            <span className={`w-8 h-8 rounded-xl grid place-items-center ${o.solid ? "bg-blue-600 text-white" : i === 1 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}><o.icon className="w-4 h-4" /></span>
            <span className="text-[12px] font-bold text-[#1F2A44]">{o.label}</span>
            {o.note && <span className="ml-auto text-[8px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded-full">{o.note}</span>}
          </div>
        ))}
      </div>
      <div className="mt-auto rounded-2xl bg-slate-50 border border-slate-100 p-3">
        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">La tua struttura</p>
        {[["Nome", "Casa Aurora"], ["Città", "Milano, Italia"]].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-1"><span className="text-[11px] text-slate-400">{k}</span><span className="text-[11px] font-semibold text-slate-700">{v}</span></div>
        ))}
      </div>
    </div>
  );
}

// 2. Publish
function ScenePublish() {
  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-center">
      <div className="flex items-center gap-1.5 mb-4">
        <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Pubblicata</span>
        <span className="inline-flex items-center gap-1 bg-white text-slate-600 text-[10px] font-bold px-2.5 py-1 rounded-full border border-slate-200"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online</span>
      </div>
      <div className="w-32 h-32 rounded-2xl bg-white border border-slate-200 grid place-items-center shadow-sm">
        <QrCode className="w-24 h-24 text-slate-900" strokeWidth={1.3} />
      </div>
      <p className="mt-4 text-[12px] font-semibold text-slate-800">stayguide.it/g/casa-aurora</p>
      <div className="mt-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 text-white text-[11px] font-semibold px-3 py-1.5"><Copy className="w-3 h-3" /> Copia link</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-600 text-[11px] font-semibold px-3 py-1.5"><Share2 className="w-3 h-3" /> Condividi</span>
      </div>
    </div>
  );
}

// 3. Guest opens the guide (the real product home)
function SceneGuest() {
  const quick = [["Wi-Fi", Wifi], ["Check-in", DoorOpen], ["Indicazioni", MapPin], ["Host", MessageCircle]];
  const cards = [["Check-in", DoorOpen], ["Wi-Fi", Wifi], ["Guida della casa", HomeIcon]];
  return (
    <div className="h-full overflow-hidden">
      <div className="relative h-32">
        <img src={DEMO.cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(15,23,42,.8),transparent)" }} />
        <div className="absolute bottom-2.5 left-3 text-white">
          <p className="font-script text-2xl leading-none">Benvenuti a</p>
          <p className="font-serif-display text-base leading-tight">Casa Aurora</p>
          <p className="text-[9px] opacity-90 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />Milano</p>
        </div>
      </div>
      <div className="px-3 pt-3 grid grid-cols-4 gap-1.5">
        {quick.map(([l, I]) => (
          <div key={l} className="text-center">
            <div className="w-9 h-9 rounded-xl grid place-items-center mx-auto" style={{ background: "#2E7CB822", color: "#2E7CB8" }}><I className="w-4 h-4" /></div>
            <span className="block text-[8px] font-semibold mt-1 text-[#1F2A44]">{l}</span>
          </div>
        ))}
      </div>
      <p className="px-3 pt-3.5 font-serif-display text-base text-[#1F2A44]">Il tuo soggiorno</p>
      <div className="px-3 pt-1.5 space-y-2">
        {cards.map(([l, I]) => (
          <div key={l} className="flex items-center gap-2.5 rounded-2xl bg-white border border-[#F0EDE6] p-2.5">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "#2E7CB822", color: "#2E7CB8" }}><I className="w-4 h-4" /></div>
            <span className="text-[12px] font-semibold text-[#1F2A44]">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 4. AI Assistant answers
function SceneAI() {
  const msgs = [
    ["u", "Qual è la password del Wi-Fi?"],
    ["a", "La rete è CasaAurora_Guest. Ecco la password 👇"],
    ["copy", "Copia password"],
    ["u", "Dove possiamo cenare stasera?"],
    ["a", "L'host consiglia l'Osteria del Centro, a 8 minuti a piedi."],
    ["map", "Apri sulla mappa"],
  ];
  return (
    <div className="h-full flex flex-col">
      <div className="bg-slate-900 text-white px-3 py-3 flex items-center gap-2 shrink-0">
        <div className="w-7 h-7 rounded-full bg-blue-500 grid place-items-center"><Sparkles className="w-3.5 h-3.5" /></div>
        <div><p className="text-[12px] font-semibold">Concierge AI</p><p className="text-[9px] text-emerald-300">● online</p></div>
      </div>
      <div className="p-3 space-y-2 flex-1 overflow-hidden">
        {msgs.map(([who, txt], i) => {
          if (who === "copy") return <button key={i} className="ml-auto flex items-center gap-1.5 rounded-full bg-[#2E7CB8] text-white text-[10px] font-semibold px-3 py-1.5"><Copy className="w-3 h-3" />{txt}</button>;
          if (who === "map") return <button key={i} className="flex items-center gap-1.5 rounded-full bg-white border border-[#2E7CB8] text-[#2E7CB8] text-[10px] font-semibold px-3 py-1.5"><MapPin className="w-3 h-3" />{txt}</button>;
          return <div key={i} className={`max-w-[82%] rounded-2xl px-3 py-2 text-[11px] leading-snug ${who === "u" ? "ml-auto bg-[#2E7CB8] text-white rounded-br-sm" : "bg-white border border-[#F0EDE6] text-[#1F2A44] rounded-bl-sm"}`}>{txt}</div>;
        })}
      </div>
    </div>
  );
}

const STEPS = [
  { t: "Crea la tua struttura", d: "Parti dall'AI, importa il tuo annuncio o inserisci i dati a mano.", scene: SceneCreate },
  { t: "Pubblica e condividi", d: "La guida va online con un link e un QR code pronti da condividere.", scene: ScenePublish },
  { t: "L'ospite apre dal telefono", d: "Nessuna app: tutte le info a portata di tocco, in un istante.", scene: SceneGuest },
  { t: "Il Concierge AI risponde", d: "Domande su Wi-Fi, check-in o dintorni? Risposte immediate, 24/7.", scene: SceneAI },
];

/* ---------- The guided walkthrough ---------- */
function DemoPlayer({ onClose }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % STEPS.length), STEP_MS);
    return () => clearTimeout(t);
  }, [index, playing]);

  const goTo = (i) => { setIndex((i + STEPS.length) % STEPS.length); setPlaying(false); };
  const Scene = STEPS[index].scene;

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="demo-player"
      style={{ background: "radial-gradient(130% 130% at 50% 0%, #1e3a8a 0%, #0f172a 62%)" }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-5 pt-3.5 pb-2 shrink-0">
        <div className="flex items-center gap-2 text-white">
          <span className="w-6 h-6 rounded-lg bg-white/15 grid place-items-center"><Sparkles className="w-3.5 h-3.5" /></span>
          <span className="text-[12px] sm:text-sm font-semibold">Come funziona StayGuide</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setPlaying((p) => !p)} data-testid="demo-playpause" aria-label={playing ? "Pausa" : "Riproduci"} className="w-8 h-8 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={onClose} data-testid="demo-close" aria-label="Chiudi" className="w-8 h-8 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile step bar */}
      <div className="sm:hidden px-4 pb-1 shrink-0">
        <div className="flex items-center">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
              <button onClick={() => goTo(i)} data-testid={`demo-step-${i}`} aria-label={`Passo ${i + 1}`}
                className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold transition-all shrink-0 ${i === index ? "bg-white text-blue-700 scale-110" : i < index ? "bg-blue-400/70 text-white" : "bg-white/15 text-white/70"}`}>{i + 1}</button>
              {i < STEPS.length - 1 && <div className="flex-1 h-0.5 mx-1 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-blue-300 transition-all duration-500" style={{ width: i < index ? "100%" : "0%" }} /></div>}
            </div>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.p key={index} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="mt-2.5 text-center text-white font-semibold text-[13px] leading-tight">
            <span className="text-blue-300">{index + 1}.</span> {STEPS[index].t}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Stage */}
      <div className="flex-1 min-h-0 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-5 px-4 sm:px-6 pb-3 sm:pb-5">
        {/* Desktop step rail */}
        <div className="hidden sm:flex flex-col justify-center w-[40%] max-w-[300px]">
          {STEPS.map((s, i) => {
            const active = i === index, done = i < index;
            return (
              <button key={i} onClick={() => goTo(i)} data-testid={`demo-step-${i}`}
                className="group relative text-left flex gap-3 py-2.5">
                {i < STEPS.length - 1 && <span className="absolute left-[15px] top-9 bottom-0 w-0.5" style={{ background: done ? "#60a5fa" : "rgba(255,255,255,0.15)" }} />}
                <span className={`relative z-10 w-8 h-8 shrink-0 rounded-full grid place-items-center text-[13px] font-bold transition-all ${active ? "bg-white text-blue-700 shadow-lg shadow-black/30 scale-105" : done ? "bg-blue-400 text-white" : "bg-white/12 text-white/70"}`}>
                  {done ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className="pt-0.5">
                  <span className={`block text-[14px] font-semibold leading-tight transition-colors ${active ? "text-white" : "text-white/70 group-hover:text-white"}`}>{s.t}</span>
                  <AnimatePresence>
                    {active && (
                      <motion.span initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="block text-[12px] text-blue-100/80 leading-snug mt-1 overflow-hidden">{s.d}</motion.span>
                    )}
                  </AnimatePresence>
                </span>
              </button>
            );
          })}
        </div>

        {/* Phone */}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div key={index} {...fade} className="h-full py-1 flex items-center justify-center">
              <Phone><Scene /></Phone>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-center gap-4 sm:gap-6 px-4 pb-4 shrink-0">
        <button onClick={() => goTo(index - 1)} data-testid="demo-prev" aria-label="Precedente" className="w-10 h-10 rounded-full bg-white/12 text-white grid place-items-center hover:bg-white/25 transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        <span className="text-[12px] font-semibold text-white/80 tabular-nums w-10 text-center">{index + 1} / {STEPS.length}</span>
        <button onClick={() => goTo(index + 1)} data-testid="demo-next" aria-label="Successivo" className="w-10 h-10 rounded-full bg-blue-500 text-white grid place-items-center hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/30"><ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

/* ---------- Section (thumbnail + lightbox) ---------- */
export function VideoDemo() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <section className="max-w-5xl mx-auto px-5 py-16" data-testid="video-demo-section">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Guarda come funziona</p>
        <h2 className="mt-2 font-display font-bold text-3xl tracking-tight">Dalla struttura alla guida, in 4 passi.</h2>
        <p className="mt-3 text-slate-500 max-w-xl mx-auto">
          Un percorso guidato: crea la struttura, pubblica con un QR, l'ospite apre dal telefono e il Concierge AI risponde a tutto.
        </p>
      </div>

      {/* Thumbnail (apre la demo guidata) */}
      <button onClick={() => setOpen(true)} data-testid="video-demo-play" aria-label="Guarda la demo guidata"
        className="group mt-8 block w-full relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5 bg-slate-900 aspect-video outline-none focus-visible:ring-4 focus-visible:ring-blue-400/40">
        <div className="absolute inset-0" style={{ background: "radial-gradient(130% 130% at 50% 0%, #1e3a8a 0%, #0f172a 62%)" }} />
        {/* mini 4-step preview */}
        <div className="absolute inset-0 flex items-center justify-center gap-5 px-6 pointer-events-none">
          <div className="h-[74%] aspect-[9/19] rounded-[1.5rem] bg-slate-900 p-1.5 shadow-2xl shadow-black/40 transition-transform duration-300 group-hover:scale-[1.02]">
            <div className="w-full h-full rounded-[1.2rem] overflow-hidden bg-[#FDFBF7]"><SceneGuest /></div>
          </div>
          <div className="hidden sm:flex flex-col gap-2.5">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-full bg-white/90 text-blue-700 grid place-items-center text-[12px] font-bold shrink-0">{i + 1}</span>
                <span className="text-[12px] font-semibold text-white/90">{s.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
        <span className="absolute inset-0 grid place-items-center">
          <span className="w-20 h-20 rounded-full bg-white/95 grid place-items-center shadow-2xl transition-transform duration-200 group-hover:scale-110 group-active:scale-95">
            <Play className="w-8 h-8 text-blue-600 fill-blue-600 ml-1" />
          </span>
        </span>
        <span className="absolute bottom-4 left-5 text-left text-white pointer-events-none">
          <span className="block font-semibold text-sm sm:text-base drop-shadow">Guarda la demo guidata</span>
          <span className="block text-xs text-blue-200">Tocca per vedere i 4 passi, al tuo ritmo</span>
        </span>
      </button>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)} data-testid="video-demo-lightbox"
            className="fixed inset-0 z-[80] bg-slate-950/90 backdrop-blur-sm grid place-items-center p-3 sm:p-8">
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl h-[88vh] sm:h-auto sm:aspect-video sm:max-h-[86vh] rounded-2xl overflow-hidden shadow-2xl shadow-black/50 bg-slate-900">
              <DemoPlayer onClose={() => setOpen(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-7 text-center">
        <p className="text-slate-500">Ti bastano pochi minuti per creare la tua prima guida.</p>
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/signup" data-testid="video-demo-cta" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-6 py-3.5 shadow-lg shadow-blue-500/25 hover:bg-blue-600 transition-colors">
            {CTA_PRIMARY} <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="video-demo-secondary" className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1 hover:text-slate-900 transition-colors">
            Scopri tutte le funzionalità <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
